import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, InputGroup, Form,
    Table, Badge, Nav, Tab, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { formatDate, formatDateTime, formatCurrency } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function Tracabilite() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [historique, setHistorique] = useState(null);
    const [stats, setStats] = useState(null);
    const [lots, setLots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('chrono');
    const [filtres, setFiltres] = useState({
        date_debut: '',
        date_fin: '',
        type: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    // Recherche de produits
    const handleSearch = async () => {
        if (!searchTerm.trim()) return;
        
        setSearching(true);
        setError(null);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/tracabilite.php?action=rechercher&q=${encodeURIComponent(searchTerm)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur recherche");
            
            const result = await response.json();
            setSearchResults(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setSearching(false);
        }
    };

    // Sélection d'un produit
    const handleSelectProduct = async (produit) => {
        setSelectedProduct(produit);
        setSearchResults([]);
        setSearchTerm('');
        await loadHistorique(produit.id_produit);
        await loadStats(produit.id_produit);
        await loadLots(produit.id_produit);
    };

    // Charger l'historique
    const loadHistorique = async (id_produit) => {
        setLoading(true);
        
        try {
            const token = localStorage.getItem("token");
            let url = `https://sawd.julesferry-ga.com/api/tracabilite.php?action=historique&id_produit=${id_produit}`;
            
            const params = new URLSearchParams();
            if (filtres.date_debut) params.append('date_debut', filtres.date_debut);
            if (filtres.date_fin) params.append('date_fin', filtres.date_fin);
            if (filtres.type) params.append('type', filtres.type);
            
            if (params.toString()) url += '&' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setHistorique(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Charger les statistiques
    const loadStats = async (id_produit) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/tracabilite.php?action=stats&id_produit=${id_produit}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    // Charger les lots
    const loadLots = async (id_produit) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/tracabilite.php?action=lots&id_produit=${id_produit}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setLots(result.data || []);
            }
        } catch (err) {
            console.error("Erreur lots", err);
        }
    };

    // Appliquer les filtres
    const applyFilters = () => {
        if (selectedProduct) {
            loadHistorique(selectedProduct.id_produit);
        }
    };

    // Réinitialiser les filtres
    const resetFilters = () => {
        setFiltres({
            date_debut: '',
            date_fin: '',
            type: ''
        });
        if (selectedProduct) {
            loadHistorique(selectedProduct.id_produit);
        }
    };

    // Obtenir le badge de type de mouvement
    const getTypeBadge = (type) => {
        const configs = {
            entree: { bg: 'success', icon: 'ri-arrow-down-line', label: 'Entrée' },
            sortie: { bg: 'danger', icon: 'ri-arrow-up-line', label: 'Sortie' },
            inventaire: { bg: 'info', icon: 'ri-survey-line', label: 'Inventaire' },
            commande_fournisseur: { bg: 'primary', icon: 'ri-truck-line', label: 'Commande fournisseur' },
            facture: { bg: 'warning', icon: 'ri-file-list-line', label: 'Facture' },
            bon_livraison: { bg: 'secondary', icon: 'ri-truck-line', label: 'Bon de livraison' }
        };
        const config = configs[type] || { bg: 'secondary', icon: 'ri-question-line', label: type };
        
        return (
            <Badge 
                bg={config.bg}
                className="px-3 py-2 rounded-pill"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.bg}), var(--bs-${config.bg}-dark))`,
                    border: 'none'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    // Fusionner tous les événements pour la vue chronologique
    const getChronologie = () => {
        if (!historique) return [];
        
        const evenements = [];
        
        // Mouvements de stock
        historique.mouvements?.forEach(m => {
            evenements.push({
                date: m.date_mouvement,
                type: 'mouvement',
                sous_type: m.type_mouvement,
                titre: `${m.type_mouvement === 'entree' ? '➕' : '➖'} Mouvement de stock`,
                description: `${m.type_mouvement === 'entree' ? 'Entrée' : 'Sortie'} de ${m.quantite} unités`,
                document: m.reference_document,
                document_type: m.type_document,
                quantite: m.quantite,
                stock_apres: m.stock_apres,
                utilisateur: m.utilisateur,
                couleur: m.type_mouvement === 'entree' ? 'success' : 'danger',
                icone: m.type_mouvement === 'entree' ? 'ri-arrow-down-line' : 'ri-arrow-up-line',
                id: m.id_mouvement
            });
        });

        // Commandes fournisseurs
        historique.commandes?.forEach(c => {
            evenements.push({
                date: c.date_commande,
                type: 'document',
                sous_type: 'commande',
                titre: '📦 Commande fournisseur',
                description: `Commande chez ${c.tiers_nom}`,
                document: c.numero_commande,
                document_type: 'commande_fournisseur',
                quantite: c.quantite,
                tiers: c.tiers_nom,
                couleur: 'primary',
                icone: 'ri-truck-line',
                id: c.id_commande_fournisseur
            });
        });

        // Factures
        historique.factures?.forEach(f => {
            evenements.push({
                date: f.date_facture,
                type: 'document',
                sous_type: 'facture',
                titre: '🧾 Facture client',
                description: `Facture pour ${f.tiers_nom}`,
                document: f.numero_facture,
                document_type: 'facture',
                quantite: f.quantite,
                tiers: f.tiers_nom,
                couleur: 'warning',
                icone: 'ri-file-list-line',
                id: f.id_facture
            });
        });

        // Bons de livraison
        historique.bons_livraison?.forEach(b => {
            evenements.push({
                date: b.date_document,
                type: 'document',
                sous_type: 'bl',
                titre: '📦 Bon de livraison',
                description: `Livraison pour ${b.tiers_nom}`,
                document: b.numero_bl,
                document_type: 'bon_livraison',
                quantite: b.quantite,
                tiers: b.tiers_nom,
                lot: b.lot,
                date_peremption: b.date_peremption,
                couleur: 'secondary',
                icone: 'ri-truck-line',
                id: b.id_bon_livraison
            });
        });

        // Inventaires
        historique.inventaires?.forEach(i => {
            evenements.push({
                date: i.date_inventaire,
                type: 'inventaire',
                titre: '📋 Inventaire',
                description: `Écart: ${i.ecart > 0 ? '+' : ''}${i.ecart}`,
                document: i.reference_inventaire,
                document_type: 'inventaire',
                quantite_theorique: i.quantite_theorique,
                quantite_reelle: i.quantite_reelle,
                ecart: i.ecart,
                utilisateur: i.utilisateur,
                couleur: i.ecart === 0 ? 'success' : i.ecart > 0 ? 'info' : 'danger',
                icone: 'ri-survey-line',
                id: i.id_inventaire
            });
        });

        // Trier par date décroissante
        return evenements.sort((a, b) => new Date(b.date) - new Date(a.date));
    };

    const chronologie = getChronologie();

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Fil d'Ariane */}
                    <Row className="mb-3">
                        <Col md={12}>
                            <div className="d-flex align-items-center gap-2">
                                <Button 
                                    variant="link" 
                                    className="p-0 text-muted" 
                                    onClick={() => navigate('/stock/etat/instantane')}
                                >
                                    <i className="ri-arrow-left-line me-1"></i>Stock
                                </Button>
                                <span className="text-muted">/</span>
                                <span className="text-primary">Traçabilité</span>
                            </div>
                        </Col>
                    </Row>

                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-4">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '80px', 
                                            height: '80px', 
                                            borderRadius: '24px',
                                            background: 'linear-gradient(135deg, #4361ee 0%, #3a56d4 100%)',
                                            boxShadow: '0 15px 25px -8px rgba(67, 97, 238, 0.4)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <i className="ri-search-eye-line text-white" style={{ fontSize: '40px' }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-2" style={{ fontSize: '2.2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Traçabilité
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Suivez le cycle de vie complet de vos produits
                                        </p>
                                    </div>
                                </div>
                                {selectedProduct && (
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => setSelectedProduct(null)}
                                        className="rounded-pill px-4"
                                    >
                                        <i className="ri-close-line me-2"></i>
                                        Changer de produit
                                    </Button>
                                )}
                            </div>
                        </Col>
                    </Row>

                    {/* Barre de recherche */}
                    {!selectedProduct && (
                        <Card className="border-0 shadow-lg mb-4" style={{ borderRadius: '16px' }}>
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    <Col md={10}>
                                        <InputGroup className="search-input" size="lg">
                                            <InputGroup.Text className="bg-white border-end-0 rounded-start-3">
                                                <i className="ri-search-line text-primary"></i>
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Rechercher par référence, désignation ou code-barres..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                                className="border-start-0 py-3"
                                            />
                                            <Button 
                                                variant="primary" 
                                                onClick={handleSearch}
                                                disabled={searching}
                                                className="px-4 rounded-end-3"
                                                style={{
                                                    background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                    border: 'none'
                                                }}
                                            >
                                                {searching ? <Spinner size="sm" /> : 'Rechercher'}
                                            </Button>
                                        </InputGroup>
                                    </Col>
                                    <Col md={2} className="d-flex align-items-center">
                                        <div className="text-muted small">
                                            <i className="ri-information-line me-1"></i>
                                            Saisissez au moins 2 caractères
                                        </div>
                                    </Col>
                                </Row>

                                {/* Résultats de recherche */}
                                {searchResults.length > 0 && (
                                    <div className="mt-4">
                                        <h6 className="mb-3">Résultats de recherche</h6>
                                        <Row className="g-3">
                                            {searchResults.map(p => (
                                                <Col md={6} key={p.id_produit}>
                                                    <Card 
                                                        className="border-0 shadow-sm product-card"
                                                        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                                        onClick={() => handleSelectProduct(p)}
                                                    >
                                                        <Card.Body className="p-3 d-flex align-items-center">
                                                            <div className="bg-light rounded-3 p-3 me-3">
                                                                <i className="ri-barcode-line text-primary fs-4"></i>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex justify-content-between align-items-start">
                                                                    <div>
                                                                        <h6 className="mb-1 fw-semibold">{p.designation}</h6>
                                                                        <div className="d-flex gap-2 mb-2">
                                                                            <Badge bg="light" text="dark">{p.reference}</Badge>
                                                                            {p.nom_categorie && (
                                                                                <Badge bg="light" text="dark">{p.nom_categorie}</Badge>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <Badge bg="primary" className="rounded-pill px-3">
                                                                        Stock: {p.stock_actuel || 0}
                                                                    </Badge>
                                                                </div>
                                                                <div className="d-flex justify-content-between small text-muted">
                                                                    <span>
                                                                        <i className="ri-price-tag-3-line me-1"></i>
                                                        Achat: {formatCurrency(p.prix_achat_ht)}
                                                                    </span>
                                                                    <span>
                                                                        <i className="ri-price-tag-line me-1"></i>
                                                        Vente: {formatCurrency(p.prix_vente_ht)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}

                                {searchTerm && searchResults.length === 0 && !searching && (
                                    <div className="text-center py-4">
                                        <i className="ri-search-line fs-1 text-muted mb-3"></i>
                                        <h6>Aucun produit trouvé</h6>
                                        <p className="text-muted small">Essayez avec d'autres mots-clés</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue détaillée du produit sélectionné */}
                    {selectedProduct && (
                        <>
                            {/* En-tête produit */}
                            <Card className="border-0 shadow-lg mb-4" style={{ borderRadius: '16px' }}>
                                <Card.Body className="p-4">
                                    <Row>
                                        <Col md={8}>
                                            <div className="d-flex align-items-start gap-3">
                                                <div className="bg-primary bg-opacity-10 p-3 rounded-3">
                                                    <i className="ri-barcode-line text-primary fs-2"></i>
                                                </div>
                                                <div>
                                                    <h2 className="fw-bold mb-2">{selectedProduct.designation}</h2>
                                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                                        <Badge bg="light" text="dark" className="px-3 py-2">
                                                            <i className="ri-barcode-line me-1"></i>
                                                            {selectedProduct.reference}
                                                        </Badge>
                                                        {selectedProduct.nom_categorie && (
                                                            <Badge bg="light" text="dark" className="px-3 py-2">
                                                                <i className="ri-folder-line me-1"></i>
                                                                {selectedProduct.nom_categorie}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={4} className="text-end">
                                            <div className="d-flex justify-content-end gap-3">
                                                <div className="text-center">
                                                    <small className="text-muted">Stock actuel</small>
                                                    <h3 className="text-primary mb-0">{selectedProduct.stock_actuel || 0}</h3>
                                                </div>
                                                <div className="text-center">
                                                    <small className="text-muted">Valeur</small>
                                                    <h3 className="text-success mb-0">
                                                        {formatCurrency((selectedProduct.stock_actuel || 0) * selectedProduct.prix_achat_ht)}
                                                    </h3>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Statistiques */}
                            {stats && (
                                <Row className="g-3 mb-4">
                                    <Col md={3}>
                                        <Card className="border-0 shadow-sm h-100">
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                                                        <i className="ri-stack-line text-primary fs-4"></i>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted">Total mouvements</small>
                                                        <h4 className="mb-0 fw-bold">{stats.total?.total_mouvements || 0}</h4>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card className="border-0 shadow-sm h-100">
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                                                        <i className="ri-arrow-down-line text-success fs-4"></i>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted">Entrées</small>
                                                        <h4 className="mb-0 fw-bold text-success">{stats.total?.entrees || 0}</h4>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card className="border-0 shadow-sm h-100">
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-danger bg-opacity-10 p-3 rounded-3 me-3">
                                                        <i className="ri-arrow-up-line text-danger fs-4"></i>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted">Sorties</small>
                                                        <h4 className="mb-0 fw-bold text-danger">{stats.total?.sorties || 0}</h4>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card className="border-0 shadow-sm h-100">
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                                                        <i className="ri-calendar-line text-info fs-4"></i>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted">Premier mouvement</small>
                                                        <h6 className="mb-0">
                                                            {stats.premier_mouvement?.premier_mouvement 
                                                                ? formatDate(stats.premier_mouvement.premier_mouvement)
                                                                : '-'}
                                                        </h6>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            )}

                            {/* Filtres */}
                            <Card className="filter-card border-0 mb-4">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0">
                                            <i className="ri-filter-line me-2 text-primary"></i>
                                            Filtres
                                        </h6>
                                        <Button
                                            variant="link"
                                            className="text-muted p-0"
                                            onClick={() => setShowFilters(!showFilters)}
                                        >
                                            <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line me-1`}></i>
                                            {showFilters ? 'Masquer' : 'Afficher'}
                                        </Button>
                                    </div>
                                    
                                    {showFilters && (
                                        <Row className="g-3">
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="small">Date début</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={filtres.date_debut}
                                                        onChange={(e) => setFiltres({...filtres, date_debut: e.target.value})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="small">Date fin</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={filtres.date_fin}
                                                        onChange={(e) => setFiltres({...filtres, date_fin: e.target.value})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="small">Type</Form.Label>
                                                    <Form.Select
                                                        value={filtres.type}
                                                        onChange={(e) => setFiltres({...filtres, type: e.target.value})}
                                                    >
                                                        <option value="">Tous</option>
                                                        <option value="entree">Entrées</option>
                                                        <option value="sortie">Sorties</option>
                                                        <option value="inventaire">Inventaires</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3} className="d-flex align-items-end">
                                                <div className="d-flex gap-2 w-100">
                                                    <Button variant="primary" onClick={applyFilters} className="flex-grow-1">
                                                        <i className="ri-filter-line me-2"></i>
                                                        Appliquer
                                                    </Button>
                                                    <Button variant="outline-secondary" onClick={resetFilters}>
                                                        <i className="ri-refresh-line"></i>
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Onglets */}
                            <Card className="border-0 shadow-lg">
                                <Card.Header className="bg-white border-0 pt-4">
                                    <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="border-0">
                                        <Nav.Item>
                                            <Nav.Link 
                                                eventKey="chrono" 
                                                className="border-0 px-4 py-3 rounded-3"
                                                style={{
                                                    color: activeTab === 'chrono' ? '#4361ee' : '#64748b',
                                                    fontWeight: 500,
                                                    background: activeTab === 'chrono' ? 'rgba(67, 97, 238, 0.05)' : 'transparent'
                                                }}
                                            >
                                                <i className="ri-timeline-line me-2"></i>
                                                Chronologie
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link 
                                                eventKey="mouvements" 
                                                className="border-0 px-4 py-3 rounded-3"
                                                style={{
                                                    color: activeTab === 'mouvements' ? '#10b981' : '#64748b',
                                                    fontWeight: 500,
                                                    background: activeTab === 'mouvements' ? 'rgba(16, 185, 129, 0.05)' : 'transparent'
                                                }}
                                            >
                                                <i className="ri-stack-line me-2"></i>
                                                Mouvements
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link 
                                                eventKey="documents" 
                                                className="border-0 px-4 py-3 rounded-3"
                                                style={{
                                                    color: activeTab === 'documents' ? '#f59e0b' : '#64748b',
                                                    fontWeight: 500,
                                                    background: activeTab === 'documents' ? 'rgba(245, 158, 11, 0.05)' : 'transparent'
                                                }}
                                            >
                                                <i className="ri-file-list-line me-2"></i>
                                                Documents
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link 
                                                eventKey="lots" 
                                                className="border-0 px-4 py-3 rounded-3"
                                                style={{
                                                    color: activeTab === 'lots' ? '#0dcaf0' : '#64748b',
                                                    fontWeight: 500,
                                                    background: activeTab === 'lots' ? 'rgba(13, 202, 240, 0.05)' : 'transparent'
                                                }}
                                            >
                                                <i className="ri-barcode-line me-2"></i>
                                                Lots & Séries
                                                {lots.length > 0 && (
                                                    <Badge bg="info" className="ms-2 rounded-pill px-3 py-1">
                                                        {lots.length}
                                                    </Badge>
                                                )}
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Tab.Content>
                                        {/* Onglet Chronologie */}
                                        <Tab.Pane active={activeTab === 'chrono'}>
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" />
                                                </div>
                                            ) : chronologie.length > 0 ? (
                                                <div className="timeline">
                                                    {chronologie.map((event, index) => (
                                                        <div key={index} className="timeline-item mb-4" style={{
                                                            animation: `slideIn 0.3s ease ${index * 0.05}s both`
                                                        }}>
                                                            <div className="d-flex">
                                                                <div className="timeline-marker me-4">
                                                                    <div className={`bg-${event.couleur} bg-opacity-10 rounded-circle p-3`}>
                                                                        <i className={`${event.icone} text-${event.couleur} fs-5`}></i>
                                                                    </div>
                                                                </div>
                                                                <div className="timeline-content flex-grow-1">
                                                                    <Card className="border-0 shadow-sm">
                                                                        <Card.Body className="p-4">
                                                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                                                <div>
                                                                                    <h6 className="mb-1">{event.titre}</h6>
                                                                                    <div className="d-flex gap-2 mb-2">
                                                                                        {getTypeBadge(event.sous_type || event.type)}
                                                                                        <small className="text-muted">
                                                                                            <i className="ri-time-line me-1"></i>
                                                                                            {formatDateTime(event.date)}
                                                                                        </small>
                                                                                    </div>
                                                                                </div>
                                                                                {event.document && (
                                                                                    <Badge bg="light" text="dark" className="px-3 py-2">
                                                                                        {event.document}
                                                                                    </Badge>
                                                                                )}
                                                                            </div>
                                                                            
                                                                            <p className="text-muted mb-3">{event.description}</p>
                                                                            
                                                                            <Row className="g-3">
                                                                                {event.quantite && (
                                                                                    <Col md={3}>
                                                                                        <small className="text-muted">Quantité</small>
                                                                                        <div className="fw-bold">{event.quantite}</div>
                                                                                    </Col>
                                                                                )}
                                                                                {event.stock_apres !== undefined && (
                                                                                    <Col md={3}>
                                                                                        <small className="text-muted">Stock après</small>
                                                                                        <div className="fw-bold text-primary">{event.stock_apres}</div>
                                                                                    </Col>
                                                                                )}
                                                                                {event.ecart !== undefined && (
                                                                                    <Col md={3}>
                                                                                        <small className="text-muted">Écart</small>
                                                                                        <div className={`fw-bold ${event.ecart > 0 ? 'text-success' : event.ecart < 0 ? 'text-danger' : ''}`}>
                                                                                            {event.ecart > 0 ? '+' : ''}{event.ecart}
                                                                                        </div>
                                                                                    </Col>
                                                                                )}
                                                                                {event.lot && (
                                                                                    <Col md={3}>
                                                                                        <small className="text-muted">Lot</small>
                                                                                        <div className="fw-bold">{event.lot}</div>
                                                                                    </Col>
                                                                                )}
                                                                                {event.utilisateur && (
                                                                                    <Col md={3}>
                                                                                        <small className="text-muted">Utilisateur</small>
                                                                                        <div className="fw-bold">{event.utilisateur}</div>
                                                                                    </Col>
                                                                                )}
                                                                            </Row>
                                                                        </Card.Body>
                                                                    </Card>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="ri-history-line fs-1 text-muted mb-3"></i>
                                                    <h5>Aucun événement trouvé</h5>
                                                </div>
                                            )}
                                        </Tab.Pane>

                                        {/* Onglet Mouvements */}
                                        <Tab.Pane active={activeTab === 'mouvements'}>
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" />
                                                </div>
                                            ) : historique?.mouvements?.length > 0 ? (
                                                <div className="table-responsive">
                                                    <Table hover className="mb-0 align-middle">
                                                        <thead className="bg-light">
                                                            <tr>
                                                                <th>Date</th>
                                                                <th>Type</th>
                                                                <th className="text-end">Quantité</th>
                                                                <th className="text-center">Stock avant</th>
                                                                <th className="text-center">Stock après</th>
                                                                <th>Document</th>
                                                                <th>Utilisateur</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {historique.mouvements.map((m, index) => (
                                                                <tr key={m.id_mouvement} style={{
                                                                    animation: `fadeIn 0.3s ease ${index * 0.03}s both`
                                                                }}>
                                                                    <td>{formatDateTime(m.date_mouvement)}</td>
                                                                    <td>{getTypeBadge(m.type_mouvement)}</td>
                                                                    <td className="text-end fw-bold">
                                                                        <span className={m.type_mouvement === 'entree' ? 'text-success' : 'text-danger'}>
                                                                            {m.type_mouvement === 'entree' ? '+' : '-'}{m.quantite}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">{m.stock_avant}</td>
                                                                    <td className="text-center fw-bold text-primary">{m.stock_apres}</td>
                                                                    <td>
                                                                        {m.reference_document && (
                                                                            <Badge bg="info" className="me-1">
                                                                                {m.type_document}
                                                                            </Badge>
                                                                        )}
                                                                        <small className="text-muted">{m.reference_document || '-'}</small>
                                                                    </td>
                                                                    <td>{m.utilisateur || '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="ri-stack-line fs-1 text-muted mb-3"></i>
                                                    <h5>Aucun mouvement trouvé</h5>
                                                </div>
                                            )}
                                        </Tab.Pane>

                                        {/* Onglet Documents */}
                                        <Tab.Pane active={activeTab === 'documents'}>
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" />
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Commandes fournisseurs */}
                                                    {historique?.commandes?.length > 0 && (
                                                        <div className="mb-4">
                                                            <h6 className="mb-3">
                                                                <i className="ri-truck-line me-2 text-primary"></i>
                                                                Commandes fournisseurs
                                                            </h6>
                                                            <div className="table-responsive">
                                                                <Table hover size="sm">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>N° Commande</th>
                                                                            <th>Date</th>
                                                                            <th>Fournisseur</th>
                                                                            <th className="text-end">Quantité</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {historique.commandes.map((c, i) => (
                                                                            <tr key={i}>
                                                                                <td>
                                                                                    <Link to={`/achats/commandes/${c.id_commande_fournisseur}`} className="text-primary">
                                                                                        {c.numero_commande}
                                                                                    </Link>
                                                                                </td>
                                                                                <td>{formatDate(c.date_commande)}</td>
                                                                                <td>{c.tiers_nom}</td>
                                                                                <td className="text-end fw-bold text-success">{c.quantite}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Factures */}
                                                    {historique?.factures?.length > 0 && (
                                                        <div className="mb-4">
                                                            <h6 className="mb-3">
                                                                <i className="ri-file-list-line me-2 text-warning"></i>
                                                                Factures clients
                                                            </h6>
                                                            <div className="table-responsive">
                                                                <Table hover size="sm">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>N° Facture</th>
                                                                            <th>Date</th>
                                                                            <th>Client</th>
                                                                            <th className="text-end">Quantité</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {historique.factures.map((f, i) => (
                                                                            <tr key={i}>
                                                                                <td>
                                                                                    <Link to={`/ventes/facturesDetails/${f.id_facture}`} className="text-primary">
                                                                                        {f.numero_facture}
                                                                                    </Link>
                                                                                </td>
                                                                                <td>{formatDate(f.date_facture)}</td>
                                                                                <td>{f.tiers_nom}</td>
                                                                                <td className="text-end fw-bold text-danger">{f.quantite}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Bons de livraison */}
                                                    {historique?.bons_livraison?.length > 0 && (
                                                        <div className="mb-4">
                                                            <h6 className="mb-3">
                                                                <i className="ri-truck-line me-2 text-info"></i>
                                                                Bons de livraison
                                                            </h6>
                                                            <div className="table-responsive">
                                                                <Table hover size="sm">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>N° BL</th>
                                                                            <th>Date</th>
                                                                            <th>Client</th>
                                                                            <th className="text-end">Quantité</th>
                                                                            <th>Lot</th>
                                                                            <th>Péremption</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {historique.bons_livraison.map((b, i) => (
                                                                            <tr key={i}>
                                                                                <td>
                                                                                    <Link to={`/ventes/bl/${b.id_bon_livraison}`} className="text-primary">
                                                                                        {b.numero_bl}
                                                                                    </Link>
                                                                                </td>
                                                                                <td>{formatDate(b.date_document)}</td>
                                                                                <td>{b.tiers_nom}</td>
                                                                                <td className="text-end fw-bold">{b.quantite}</td>
                                                                                <td>{b.lot || '-'}</td>
                                                                                <td>{b.date_peremption ? formatDate(b.date_peremption) : '-'}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Inventaires */}
                                                    {historique?.inventaires?.length > 0 && (
                                                        <div className="mb-4">
                                                            <h6 className="mb-3">
                                                                <i className="ri-survey-line me-2 text-success"></i>
                                                                Inventaires
                                                            </h6>
                                                            <div className="table-responsive">
                                                                <Table hover size="sm">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Réf. Inventaire</th>
                                                                            <th>Date</th>
                                                                            <th className="text-end">Théorique</th>
                                                                            <th className="text-end">Réel</th>
                                                                            <th className="text-center">Écart</th>
                                                                            <th>Validé par</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {historique.inventaires.map((i, idx) => (
                                                                            <tr key={idx}>
                                                                                <td>
                                                                                    <Link to={`/stock/inventaire/${i.id_inventaire}`} className="text-primary">
                                                                                        {i.reference_inventaire}
                                                                                    </Link>
                                                                                </td>
                                                                                <td>{formatDate(i.date_inventaire)}</td>
                                                                                <td className="text-end">{i.quantite_theorique}</td>
                                                                                <td className="text-end fw-bold">{i.quantite_reelle}</td>
                                                                                <td className="text-center">
                                                                                    <span className={`fw-bold ${i.ecart > 0 ? 'text-success' : i.ecart < 0 ? 'text-danger' : ''}`}>
                                                                                        {i.ecart > 0 ? '+' : ''}{i.ecart}
                                                                                    </span>
                                                                                </td>
                                                                                <td>{i.utilisateur || '-'}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {(!historique?.commandes?.length && 
                                                      !historique?.factures?.length && 
                                                      !historique?.bons_livraison?.length && 
                                                      !historique?.inventaires?.length) && (
                                                        <div className="text-center py-5">
                                                            <i className="ri-file-list-line fs-1 text-muted mb-3"></i>
                                                            <h5>Aucun document trouvé</h5>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </Tab.Pane>

                                        {/* Onglet Lots */}
                                        <Tab.Pane active={activeTab === 'lots'}>
                                            {lots.length > 0 ? (
                                                <Row className="g-4">
                                                    {lots.map((lot, index) => {
                                                        const estPerime = lot.date_peremption && new Date(lot.date_peremption) < new Date();
                                                        const joursRestant = lot.date_peremption 
                                                            ? Math.ceil((new Date(lot.date_peremption) - new Date()) / (1000 * 60 * 60 * 24))
                                                            : null;
                                                        
                                                        return (
                                                            <Col md={6} key={index}>
                                                                <Card className="border-0 shadow-sm h-100">
                                                                    <Card.Body className="p-4">
                                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                                            <div>
                                                                                <Badge 
                                                                                    bg={estPerime ? 'danger' : 'success'} 
                                                                                    className="mb-2 px-3 py-2 rounded-pill"
                                                                                >
                                                                                    <i className="ri-barcode-line me-1"></i>
                                                                                    Lot: {lot.lot}
                                                                                </Badge>
                                                                            </div>
                                                                            <Badge bg="primary" className="px-3 py-2 rounded-pill">
                                                                                {lot.nb_lignes} mouvement(s)
                                                                            </Badge>
                                                                        </div>
                                                                        
                                                                        <div className="mb-3">
                                                                            <div className="d-flex justify-content-between mb-2">
                                                                                <span className="text-muted">Quantité totale</span>
                                                                                <span className="fw-bold text-primary">{lot.quantite_totale}</span>
                                                                            </div>
                                                                            {lot.date_peremption && (
                                                                                <>
                                                                                    <div className="d-flex justify-content-between mb-2">
                                                                                        <span className="text-muted">Date péremption</span>
                                                                                        <span className={`fw-bold ${estPerime ? 'text-danger' : ''}`}>
                                                                                            {formatDate(lot.date_peremption)}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="d-flex justify-content-between">
                                                                                        <span className="text-muted">Statut</span>
                                                                                        {estPerime ? (
                                                                                            <Badge bg="danger" className="rounded-pill px-3 py-2">
                                                                                                <i className="ri-alert-line me-1"></i>
                                                                                                Périmé
                                                                                            </Badge>
                                                                                        ) : joursRestant && joursRestant <= 30 ? (
                                                                                            <Badge bg="warning" className="rounded-pill px-3 py-2">
                                                                                                <i className="ri-time-line me-1"></i>
                                                                                                Expire dans {joursRestant}j
                                                                                            </Badge>
                                                                                        ) : (
                                                                                            <Badge bg="success" className="rounded-pill px-3 py-2">
                                                                                                <i className="ri-check-line me-1"></i>
                                                                                Valide
                                                                                            </Badge>
                                                                                        )}
                                                                                    </div>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="ri-barcode-line fs-1 text-muted mb-3"></i>
                                                    <h5>Aucun lot ou numéro de série</h5>
                                                    <p className="text-muted">Ce produit n'a pas de traçabilité par lot</p>
                                                </div>
                                            )}
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    @keyframes slideIn {
                        from { opacity: 0; transform: translateX(-20px); }
                        to { opacity: 1; transform: translateX(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .product-card {
                        transition: all 0.3s ease;
                        border-radius: 12px;
                    }

                    .product-card:hover {
                        transform: translateY(-4px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }

                    .timeline-marker {
                        position: relative;
                    }

                    .timeline-marker::after {
                        content: '';
                        position: absolute;
                        top: 60px;
                        left: 50%;
                        width: 2px;
                        height: calc(100% + 20px);
                        background: linear-gradient(to bottom, #e2e8f0, #e2e8f0);
                        transform: translateX(-50%);
                    }

                    .timeline-item:last-child .timeline-marker::after {
                        display: none;
                    }

                    .filter-card {
                        border-radius: 12px;
                        background: #f8fafc;
                    }

                    .search-input .form-control:focus {
                        box-shadow: none;
                        border-color: #4361ee;
                    }

                    .nav-tabs .nav-link {
                        margin-right: 8px;
                        transition: all 0.3s ease;
                    }

                    .nav-tabs .nav-link:hover:not(.active) {
                        background: #f8fafc;
                        color: #1e293b;
                        transform: translateY(-2px);
                    }

                    .nav-tabs .nav-link.active {
                        box-shadow: 0 4px 12px rgba(67, 97, 238, 0.15);
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}